import type { ReactNode } from 'react';

import { motion } from 'framer-motion';
import { useRouterState } from '@tanstack/react-router';

import { motion as motionTokens } from '../../theme/motion';

// ----------------------------------------------------------------------

export type RouteTransitionProps = {
  children: ReactNode;
};

/**
 * A short fade and rise for a layout's `<Outlet>` on each route change.
 * Keyed on the leaf match id: it changes when the new content commits, not
 * when navigation starts, so a lazy route does not replay the old page.
 */
export function RouteTransition({ children }: RouteTransitionProps) {
  const matchId = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.id ?? '',
  });
  return (
    <motion.div
      key={matchId}
      initial={{ opacity: 0, y: 8, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: motionTokens.durationMs.base / 1000, ease: motionTokens.easeArray.out }}
    >
      {children}
    </motion.div>
  );
}
